import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { isDuplicate } from "../../../constants/helper";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  departmentsTooltip,
  originsTooltip,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const SpaServiceMedicalConditions = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [conditionCode, setConditionCode] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [conditionName, setConditionName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [conditionNameFieldError, setConditionNameFieldError] =
    useState<boolean>(false);
  const [conditionNameFieldErrorMessage, setConditionNameFieldErrorMessage] =
    useState<string>("");
  const [descriptionFieldError, setDescriptionFieldError] =
    useState<boolean>(false);
  const [descriptionFieldErrorMessage, setDescriptionFieldErrorMessage] =
    useState<string>("");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateMedicalConditionFields = (): boolean => {
    if (!conditionCode || conditionCode.length === 0) {
      setAlertMessage("Condition Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        conditionCode,
        validationLength.conditionCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.conditionCode);
      return false;
    }

    if (!conditionName || conditionName.length === 0) {
      setAlertMessage("Condition Description is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setConditionNameFieldError(false);
    setConditionNameFieldErrorMessage("");
    if (
      !validateString(
        conditionName,
        validationLength.conditionName,
        validationType.OTHER,
      )
    ) {
      setConditionNameFieldError(true);
      setConditionNameFieldErrorMessage(validationMessages.conditionName);
      return false;
    }

    setDescriptionFieldError(false);
    setDescriptionFieldErrorMessage("");
    if (description && description.length > 0) {
      if (
        !validateString(
          description,
          validationLength.conditionDescription,
          validationType.NAME,
        )
      ) {
        setDescriptionFieldError(true);
        setDescriptionFieldErrorMessage(
          validationMessages.conditionDescription,
        );
        return false;
      }
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.CONDITIONCODE,
        conditionCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateMedicalConditionFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${SpaPropertyEnum.serviceMedicalConditions} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateMedicalConditionFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setConditionCode(list["Condition Code"] ?? null);
      setActive(parseSpaBooleanField(list["Private Condition"], false));
      setConditionName(
        list["Condition Description"] ?? list["Condition Name"] ?? null,
      );
      setDescription(list["Condition Warning"] ?? list?.Description ?? null);
    } else {
      setConditionCode(null);
      setActive(true);
      setConditionName(null);
      setDescription(null);
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setConditionNameFieldError(false);
      setConditionNameFieldErrorMessage("");
      setDescriptionFieldError(false);
      setDescriptionFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Condition Code": conditionCode,
    "Private Condition": active,
    "Condition Description": conditionName,
    "Condition Warning": description,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Service Medical Conditions
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.serviceMedicalConditionHeader ??
                  originsTooltip?.active ??
                  "Service Medical Conditions",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Condition Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceMedicalConditionConditionCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Condition Code"
              value={conditionCode ?? ""}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              error={codeFieldError}
              onChange={(e) => setConditionCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Condition Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceMedicalConditionConditionDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Condition Description"
              value={conditionName ?? ""}
              onChange={(e) => setConditionName(e.target.value || null)}
              error={conditionNameFieldError}
              helperText={
                conditionNameFieldError ? conditionNameFieldErrorMessage : ""
              }
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Condition Warning</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceMedicalConditionConditionWarning,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Condition Warning"
              multiline
              minRows={1}
              value={description ?? ""}
              onChange={(e) => setDescription(e.target.value || null)}
              error={descriptionFieldError}
              helperText={
                descriptionFieldError ? descriptionFieldErrorMessage : ""
              }
              fullWidth
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label={
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                >
                  Private Condition
                  <Tooltip
                    title={tooltipComponent(
                      spaTooltipHeaders.serviceMedicalConditionPrivateCondition,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack>
              }
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Medical
            Condition
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">
            Service Medical Conditions Available
          </Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.CONDITIONCODE}
              name={DNDFieldsEnum.CONDITIONNAME}
            />
          ) : (
            <Typography variant="body2">
              No Service Medical Conditions Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaServiceMedicalConditions;
